import React, { useMemo, useState } from "react";
import { SEO } from "@/components/seo/SEO";
import { InvestmentGrid } from "@/components/InvestmentGrid";
import { RevenueStreamInfographic } from "@/components/RevenueStreamInfographic";

export default function CoopSnellaadparking() {
  const [kwhPerDay, setKwhPerDay] = useState(600);
  const [pvKw, setPvKw] = useState(25);
  const [batteryKwh, setBatteryKwh] = useState(100);
  const [fastChargers, setFastChargers] = useState(2);
  const [chargerKw, setChargerKw] = useState(60);

  const dailyPvYield = useMemo(() => Math.round(pvKw * 3.2), [pvKw]);
  const localShare = useMemo(() => {
    const share = Math.min(100, Math.round((dailyPvYield / Math.max(1, kwhPerDay)) * 100));
    return share;
  }, [dailyPvYield, kwhPerDay]);

  const peakPowerNeeded = useMemo(() => fastChargers * chargerKw, [fastChargers, chargerKw]);

  const batteryPeakShave = useMemo(() => {
    const oneHourPeak = peakPowerNeeded;
    const canCover = Math.min(1, batteryKwh / Math.max(1, oneHourPeak));
    return Math.round(canCover * 50);
  }, [batteryKwh, peakPowerNeeded]);

  return (
    <>
      <SEO
        title="Coöperatie Snellaadparking Pannerden - Energie & Mobiliteitshub"
        description="Coöperatief snellaadpark met zonnecarport, batterijopslag en lokale energiedeling in Pannerden. Sluit je aan bij de duurzame energietransitie."
        canonical="/coop-snellaadparking"
        keywords="coöperatie, snelladen, zonnecarport, batterijopslag, Pannerden, energietransitie, duurzaam laden"
      />
      
      <div className="min-h-screen w-full bg-gradient-to-b from-slate-900 to-slate-800 text-slate-50">
        <header className="sticky top-0 z-20 backdrop-blur supports-[backdrop-filter]:bg-slate-900/60">
          <nav className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/20 ring-1 ring-emerald-300/30">
                ⚡
              </span>
              <h1 className="text-lg font-semibold">Energie & Mobiliteitshub Pannerden</h1>
            </div>
            <a href="#meedoen" className="rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-emerald-400 transition">
              Meedoen
            </a>
          </nav>
        </header>

        <section className="mx-auto max-w-6xl px-4 pt-12 pb-8 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="mb-2 inline-block rounded-full border border-emerald-400/40 px-3 py-1 text-emerald-300 text-xs uppercase tracking-wider">Coöperatief • Innovatief • Lokaal</p>
            <h2 className="text-3xl md:text-5xl font-bold leading-tight">Snellaadparking Pannerden - Wie doet er mee?</h2>
            <p className="mt-4 text-slate-200/90">Een coöperatie van ondernemers en bewoners wil een publiek toegankelijk laadplein realiseren met <strong>1–2 snelladers</strong>, <strong>zonnecarport</strong>, en <strong>batterijopslag</strong>. Samen versnellen we de transitie en houden we de opbrengsten lokaal in Pannerden.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#impact" className="rounded-2xl bg-slate-100/10 px-4 py-2 text-sm hover:bg-slate-100/20 transition">Waarom dit werkt</a>
              <a href="#plan" className="rounded-2xl bg-slate-100/10 px-4 py-2 text-sm hover:bg-slate-100/20 transition">Plan & mijlpalen</a>
              <a href="#meedoen" className="rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-emerald-400 transition">Sluit je aan</a>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] w-full rounded-3xl bg-slate-700/50 ring-1 ring-white/10 p-1">
              <div className="h-full w-full rounded-2xl bg-gradient-to-br from-emerald-400/30 to-cyan-400/30 flex items-center justify-center text-center">
                <div className="px-8">
                  <p className="text-lg font-semibold">Conceptvisual</p>
                  <p className="text-sm opacity-90">Zonnecarport • 2× DC-snellader • Batterijopslag • Energiepooling</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="impact" className="mx-auto max-w-6xl px-4 pb-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: "Snelladers", value: `${fastChargers}× ${chargerKw} kW` },
              { label: "PV-carport", value: `${pvKw} kWp` },
              { label: "Batterij", value: `${batteryKwh} kWh` },
              { label: "Publiek toegankelijk", value: "24/7" },
            ].map((kpi, idx) => (
              <div key={idx} className="rounded-2xl bg-slate-900/50 p-4 ring-1 ring-white/10">
                <p className="text-slate-300 text-sm">{kpi.label}</p>
                <p className="text-2xl font-semibold mt-1">{kpi.value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-8" aria-labelledby="impact-heading">
          <h3 id="impact-heading" className="text-2xl font-bold mb-6">Impact in één oogopslag</h3>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Impact lijst - linkerkant */}
            <div>
              <ul className="space-y-4">
                <li className="flex gap-3 p-4 rounded-xl bg-slate-900/30 ring-1 ring-white/5">
                  <span className="mt-1">🌞</span>
                  <span><strong>Zonnecarport + bedrijven-daken</strong>: bedrijven stellen m² beschikbaar, inwoners/ondernemers kopen mee. Overdag gebruik bij bedrijven, 's avonds via batterij terug naar bewoners.</span>
                </li>
                <li className="flex gap-3 p-4 rounded-xl bg-slate-900/30 ring-1 ring-white/5">
                  <span className="mt-1">🔋</span>
                  <span><strong>Batterijopslag</strong> vangt overschotten en levert 's avonds goedkope groene stroom. Vermindert netcongestie.</span>
                </li>
                <li className="flex gap-3 p-4 rounded-xl bg-slate-900/30 ring-1 ring-white/5">
                  <span className="mt-1">🛒</span>
                  <span><strong>Lokale supermarkten slim koelen</strong>: via hub-automatisering koelen in de nacht met goedkope stroom, zodat ze bij opening al efficiënt draaien.</span>
                </li>
                <li className="flex gap-3 p-4 rounded-xl bg-slate-900/30 ring-1 ring-white/5">
                  <span className="mt-1">🚌</span>
                  <span><strong>Buurt- en elektrische bussen</strong>: voorzien in laadmogelijkheden, zodat collectief vervoer in Pannerden emissievrij kan draaien.</span>
                </li>
                <li className="flex gap-3 p-4 rounded-xl bg-slate-900/30 ring-1 ring-white/5">
                  <span className="mt-1">🎡</span>
                  <span><strong>Pannerdense Kermis op zonnestroom</strong>: grootste evenement volledig CO₂-neutraal met innovatieve energieoplossingen uit de hub.</span>
                </li>
                <li className="flex gap-3 p-4 rounded-xl bg-slate-900/30 ring-1 ring-white/5">
                  <span className="mt-1">🤝</span>
                  <span><strong>Coöperatief verdienmodel</strong>: opbrengst vloeit terug naar leden en dorpsinitiatieven.</span>
                </li>
              </ul>
            </div>

            {/* Calculator - rechterkant, max 50% breed */}
            <div>
              <div className="rounded-3xl bg-slate-900/50 p-5 ring-1 ring-white/10 sticky top-24">
                <h4 className="text-xl font-semibold">Snelle rekentool (indicatief)</h4>
                <p className="text-sm text-slate-300 mt-1 mb-4">Speel met aannames om het effect op netcapaciteit en lokale opwek te zien.</p>
                
                <div className="space-y-3">
                  <LabeledInput label="Dagverbruik laden (kWh)" value={kwhPerDay} setValue={setKwhPerDay} step={50} />
                  <LabeledInput label="PV-carport (kWp)" value={pvKw} setValue={setPvKw} step={5} />
                  <LabeledInput label="Batterij (kWh)" value={batteryKwh} setValue={setBatteryKwh} step={10} />
                  <LabeledInput label="Aantal snelladers" value={fastChargers} setValue={setFastChargers} min={1} max={4} step={1} />
                  <LabeledInput label="Vermogen per lader (kW)" value={chargerKw} setValue={setChargerKw} min={30} max={150} step={10} />
                </div>
                
                <div className="mt-5 p-4 bg-slate-800/50 rounded-xl">
                  <h5 className="text-sm font-semibold text-emerald-300 mb-3">Resultaten:</h5>
                  <div className="space-y-2">
                    <InfoRow label="Benodigde piekvermogen" value={`${peakPowerNeeded} kW`} />
                    <InfoRow label="Piekreductie door batterij" value={`~${batteryPeakShave}%`} />
                    <InfoRow label="PV-opbrengst per dag" value={`${dailyPvYield} kWh/dag`} />
                    <InfoRow label="Lokaal gedekt door zon" value={`${localShare}%`} />
                  </div>
                </div>
                
                <p className="text-xs text-slate-400 mt-3">* Indicatief; voor investeringsbeslissingen is technische en financiële engineering nodig.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h4 className="text-xl font-semibold mb-4">Locatie en 3D kaart</h4>
              <p className="text-slate-300 mb-4">Pannerden (Gemeente Zevenaar) - zoeken naar de beste locatie. Centrale plek met regionale betekenis.</p>
              <div className="rounded-2xl bg-slate-900/50 overflow-hidden ring-1 ring-white/10">
                <iframe 
                  src="https://3dbag.nl/nl/viewer?rdx=199877.58270459305&rdy=433598.12836313155&ox=233.146689565634&oy=320.2281112613632&oz=233.146689565634"
                  width="100%" 
                  height="300"
                  className="rounded-xl"
                  title="3D kaart Pannerden - Mogelijke locaties snellaadparking"
                />
                <div className="p-3 bg-slate-800/50">
                  <p className="text-xs text-slate-400">3D kaart van Pannerden - bekijk mogelijke locaties voor het snellaadplein</p>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-xl font-semibold mb-4">Investeerdersplattegrond</h4>
              <InvestmentGrid />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-8">
          <RevenueStreamInfographic />
        </section>

        <section id="plan" className="mx-auto max-w-6xl px-4 py-8">
          <h3 className="text-2xl font-bold">Plan & mijlpalen</h3>
          <ol className="mt-4 grid gap-4 md:grid-cols-2">
            {[
              { title: "1) Coöperatie & afspraken", detail: "Coöperatieve vereniging, samenwerking met bedrijven (daken/grond) en gemeente Zevenaar. Zoeken geschikte locatie in Pannerden." },
              { title: "2) Technisch & financieel ontwerp", detail: "Snelladers, zonnecarports + bedrijfsdaken, batterijopslag, automatisering supermarkten en logistieke laadinfrastructuur." },
              { title: "3) Net & vergunningen", detail: "Aanvraag/verzwaring aansluiting, verkeersbesluit voor laadplekken, bouwmelding/carport indien nodig." },
              { title: "4) Subsidies & financiering", detail: "RVO (SPRILA/MIA), Provincie Gelderland, lokale inleg van leden en bedrijfsdeelname." },
              { title: "5) Realisatie", detail: "Plaatsing carport, zonnepanelen, batterij en automatisering van koeling in winkels. Start laadinfrastructuur voor buurtbussen." },
              { title: "6) Exploitatie & community", detail: "Rapportage, coöperatietarieven, verduurzaming evenementen zoals de Pannerdense Kermis." },
            ].map((step, i) => (
              <li key={i} className="rounded-2xl bg-slate-900/50 p-4 ring-1 ring-white/10">
                <p className="text-sm text-emerald-300">Fase {i + 1}</p>
                <p className="text-lg font-semibold">{step.title}</p>
                <p className="text-slate-300 mt-1">{step.detail}</p>
              </li>
            ))}
          </ol>
        </section>


        <section id="meedoen" className="mx-auto max-w-6xl px-4 py-10">
          <div className="rounded-3xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 p-6 ring-1 ring-emerald-300/30">
            <h3 className="text-2xl font-bold">Meedoen als ondernemer of bewoner?</h3>
            <p className="mt-2 text-slate-100">Stel je dak of grond beschikbaar, koop m² zon-opbrengst of sluit aan bij de hub. Samen leveren we betaalbare groene stroom, verduurzamen we winkels en bussen, en laten we zelfs de kermis draaien op zon. Laat je gegevens achter, we sturen je de informatiekit en uitnodiging voor de eerstvolgende sessie.</p>
            <form className="mt-4 grid gap-3 md:grid-cols-3" onSubmit={(e) => e.preventDefault()}>
              <input className="rounded-xl bg-white/90 text-slate-900 px-3 py-2 placeholder-slate-500" placeholder="Naam" />
              <input type="email" className="rounded-xl bg-white/90 text-slate-900 px-3 py-2 placeholder-slate-500" placeholder="E‑mail" />
              <button className="rounded-xl bg-emerald-500 px-4 py-2 font-semibold text-slate-900 hover:bg-emerald-400 transition">Ontvang infokit</button>
            </form>
            <p className="text-xs text-slate-200/80 mt-2">Door je aan te melden, ga je akkoord met contact door de initiatiefnemers van de coöperatie. We delen je gegevens niet met derden.</p>
          </div>
        </section>

        <footer className="mx-auto max-w-6xl px-4 pb-10 text-sm text-slate-300">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <p>© {new Date().getFullYear()} Coöperatie Snellaadparking Pannerden</p>
            <div className="flex gap-4">
              <a href="#plan" className="hover:underline">Projectplan</a>
              <a href="#locatie" className="hover:underline">Locatie</a>
              <a href="#meedoen" className="hover:underline">Meedoen</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

function LabeledInput({ label, value, setValue, min = 0, max = 10000, step = 1 }) {
  return (
    <label className="grid gap-1">
      <span className="text-sm text-slate-300">{label}</span>
      <div className="flex items-center gap-2">
        <input 
          type="range" 
          min={min} 
          max={max} 
          step={step} 
          value={value} 
          onChange={(e) => setValue(Number(e.target.value))} 
          className="w-full accent-emerald-500" 
        />
        <input 
          type="number" 
          min={min} 
          max={max} 
          step={step} 
          value={value} 
          onChange={(e) => setValue(Number(e.target.value))} 
          className="w-24 rounded-lg bg-white/90 px-2 py-1 text-slate-900 text-sm"
        />
      </div>
    </label>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm text-slate-300">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}